import cn, { Argument as ClassName } from 'classnames'
import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import useDefer, { Status } from '../../common/hooks/useDefer'
import { SAlert } from '../../common/features/chargepoint-details-widget/chargepoint-details-widget.styles'
import usePatch from '../../common/hooks/usePatch'
import Text from '../../ui/Text'
import { Button } from '../button'
import {
    SFormRow,
    SInput,
} from '../../support/features/charge-point-capi-details-widget/charge-point-capi-details-widget.style'
import { IPartner } from '../loadPartners'
import { updatePartner } from '../update-partner'
import { validateHostNameFormat } from '../validate-hostname-format'
import { WidgetLayout } from '../widget-layout'
import useText from '../../common/hooks/useText'

export type PartnerDetailsWidgetProps = {
    partner: IPartner
    className?: ClassName
    style?: React.CSSProperties
}

const SActions = styled.div`
    display: flex;
    justify-content: right;
`

export function PartnerDetailsWidget({
    partner: origin,
    className,
    style,
}: PartnerDetailsWidgetProps): React.ReactElement {
    const text = useText()
    const { value: partner, apply, set } = usePatch(origin)
    useEffect(() => set(origin), [origin])

    const [error, setError] = useState('')
    const [message, setMessage] = useState('')

    const isHostNameValid = useMemo(
        () => (partner.hostName ? validateHostNameFormat(partner.hostName) : true),
        [partner.hostName]
    )

    const { execute, status } = useDefer(() => {
        setError('')
        setMessage('')
        return updatePartner(partner)
            .then(() => setMessage(text('msgPartnerUpdated')))
            .catch(() => setError(text('msgCouldNotUpdatePartner')))
    }, [partner])

    return (
        <WidgetLayout
            title={text('lblPartnerDetailsWidget', { partnerName: partner.name })}
            className={cn(className)}
            style={style}
            loadingStatus={status === Status.PENDING}
            data-testid="PartnerDetailsId"
        >
            {error && (
                <SAlert type="error" data-testid="errorMsg">
                    {error}
                </SAlert>
            )}
            {message && (
                <SAlert type="success" data-testid="successMsg">
                    {message}
                </SAlert>
            )}
            <SFormRow>
                <label htmlFor="name">
                    <Text code="lblNameField" />
                </label>
                <SInput
                    name="name"
                    id="name"
                    data-testid="name"
                    value={partner.name || ''}
                    onChange={(event) => apply({ name: event.target.value })}
                />
            </SFormRow>
            <SFormRow>
                <label htmlFor="description">
                    <Text code="lblAddress" />
                </label>
                <SInput
                    data-testid="description"
                    name="description"
                    id="description"
                    value={partner.description || ''}
                    onChange={(event) => apply({ description: event.target.value })}
                />
            </SFormRow>
            <SFormRow>
                <label htmlFor="email">
                    <Text code="lblEmailField" />
                </label>
                <SInput
                    data-testid="email"
                    name="email"
                    id="email"
                    value={partner.email || ''}
                    onChange={(event) => apply({ email: event.target.value })}
                />
            </SFormRow>
            <SFormRow>
                <label htmlFor="phone">
                    <Text code="lblPhoneField" />
                </label>
                <SInput
                    data-testid="phone"
                    name="phone"
                    id="phone"
                    value={partner.phone || ''}
                    onChange={(event) => apply({ phone: event.target.value })}
                />
            </SFormRow>
            <SFormRow>
                <label htmlFor="hostName">
                    <Text code="lblHostNameField" />
                </label>
                <SInput
                    data-testid="hostName"
                    name="hostName"
                    id="hostName"
                    value={partner.hostName || ''}
                    onChange={(event) => apply({ hostName: event.target.value })}
                    error={!isHostNameValid}
                />
                <span className="padding-top-15">
                    <b>
                        <Text code="lblExampleField" />
                    </b>
                    &nbsp;
                    <>my.charge.space</>
                </span>
            </SFormRow>
            <SActions>
                <Button
                    data-testId="btn-save"
                    title={text('btnSave')}
                    onClick={execute}
                    disabled={!isHostNameValid || !partner.hostName || status === Status.PENDING}
                    dataTestId={'savePartnerDetails'}
                />
            </SActions>
        </WidgetLayout>
    )
}
